// Location: src/features/error-monitor/components/ErrorMonitor.tsx

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ViewModule as GroupIcon,
  UnfoldMore as ExpandMetadataIcon,
  UnfoldLess as CollapseMetadataIcon,
} from '@mui/icons-material';
import { useTranslate, useGetList, usePermissions, useGetIdentity, useDataProvider, useNotify } from 'react-admin';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorEventsList from './ErrorEventList';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider } from '@/components/common/RaFilterPopover';
import { errorMonitorHelpConfig } from '@/config/helpConfig';
import { getSeverityColor, type ErrorSeverity, type ErrorCategory } from '@/features/error-definitions';
import { BulkDeletePillButton } from '@/components/common';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import type { ErrorFilters } from '../types';
import { canViewErrorMonitor } from '@/utils/auth';
import { localIsoDate, localIsoDateDaysAgo } from '@/lib/localDate';

type ViewMode = 'list' | 'grouped';

// Severity options for filter
const SEVERITY_OPTIONS: ErrorSeverity[] = ['Critical', 'Warning', 'Info'];

/** «XS-0001 · Recepción»: installation code plus the registered name when there is one. */
export const deviceLabel = (device: { installationCode: string; name?: string | null }): string =>
  device.name ? `${device.installationCode} · ${device.name}` : device.installationCode;

// Category options for filter
const CATEGORY_OPTIONS: ErrorCategory[] = ['Encoder', 'Mechanical', 'Card', 'Room', 'Network', 'System', 'PMS', 'Locks', 'QR', 'Payments', 'Identification'];

// Nature options for filter
const NATURE_OPTIONS = ['Error', 'Operational', 'Informational'] as const;

const NATURE_COLORS: Record<string, string> = {
  Error: '#F44336',
  Operational: '#FF9800',
  Informational: '#0288D1',
};

/**
 * Error Monitor Dashboard
 * Accessible to SuperAdmin and GroupAdmin
 *
 * Paginated error list with filters. Error definitions have their own section tab
 * (/error-definitions), so this screen no longer switches to them.
 */
export const ErrorMonitor = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const isSuperAdmin = permissions === 'SuperAdmin' || permissions === 'Admin';
  const isGroupAdmin = permissions === 'GroupAdmin';
  const canViewErrors = canViewErrorMonitor();
  const assignedGroups: string[] = (identity as any)?.assignedGroups || [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showGroupFilter = isSuperAdmin || isMultiGroupAdmin;

  // Bulk selection (SuperAdmin, lifted from ErrorEventsList)
  const selection = useBulkSelection();
  const [reloadKey, setReloadKey] = useState(0);

  // Active tab

  // Error list view mode
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Metadata display mode
  const [metadataExpanded, setMetadataExpanded] = useState(false);

  // Parse URL params for initial filters
  const getInitialFilters = (): ErrorFilters => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');

    let urlFilters: Partial<ErrorFilters> = {};
    if (filterParam) {
      try {
        urlFilters = JSON.parse(filterParam);
      } catch (e) {
        console.error('Error parsing filter param:', e);
      }
    }

    return {
      dateFrom: urlFilters.dateFrom || localIsoDateDaysAgo(3),
      dateTo: urlFilters.dateTo || localIsoDate(),
      groupId: urlFilters.groupId,
      hotelId: urlFilters.hotelId,
      deviceId: urlFilters.deviceId,
      severity: urlFilters.severity,
      errorCode: urlFilters.errorCode,
      category: urlFilters.category,
      nature: urlFilters.nature,
    };
  };

  const [filters, setFilters] = useState<ErrorFilters>(getInitialFilters);

  // Update filters when URL changes
  useEffect(() => {
    setFilters(getInitialFilters());
  }, [location.search]);

  // Shared multi-hotel scope selector (Decision 6/unit G, wave 3 follow-up): an
  // ADDITIONAL control alongside the existing group/hotel filters above, never
  // replacing them. When the scope narrows to a single hotel, the existing
  // `hotelId` filter is pre-filled too so both stay coherent; the `hotelIds`
  // list (comma-separated, or undefined for "all") is always merged into
  // `filters` so `ErrorEventsList`/`getErrorStats` below can send it.
  const scope = useHotelScope();
  useEffect(() => {
    const ids = scope.selectedHotelIds;
    setFilters((prev) => {
      const hotelIds = ids && ids.length > 0 ? ids.join(',') : undefined;
      if (prev.hotelIds === hotelIds && !(ids && ids.length === 1 && prev.hotelId !== ids[0])) {
        return prev;
      }
      return {
        ...prev,
        hotelIds,
        hotelId: ids && ids.length === 1 ? ids[0] : prev.hotelId,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.selectedHotelIds]);

  // Get groups list for filter (SuperAdmin sees all, multi-group GA sees assigned only)
  const { data: groups = [] } = useGetList('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: isMultiGroupAdmin ? { id: assignedGroups } : {},
  }, { enabled: showGroupFilter });

  // Get hotels list for filter (filtered by group if selected)
  const { data: hotels = [] } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    ...(filters.groupId ? { filter: { groupId: filters.groupId } } : {}),
  });

  // Get devices list for filter (filtered by hotel if selected)
  const { data: devices = [] } = useGetList('devices', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'installationCode', order: 'ASC' },
    ...(filters.hotelId ? { filter: { hotelId: filters.hotelId } } : {}),
  });

  const handleFilterChange = (field: keyof ErrorFilters, value: any) => {
    const newFilters = {
      ...filters,
      [field]: value || undefined,
    };
    setFilters(newFilters);

    // Update URL with new filters (only non-default values)
    const urlFilters: Partial<ErrorFilters> = {};
    if (newFilters.groupId) urlFilters.groupId = newFilters.groupId;
    if (newFilters.deviceId) urlFilters.deviceId = newFilters.deviceId;
    if (newFilters.hotelId) urlFilters.hotelId = newFilters.hotelId;
    if (newFilters.severity) urlFilters.severity = newFilters.severity;
    if (newFilters.errorCode) urlFilters.errorCode = newFilters.errorCode;
    if (newFilters.category) urlFilters.category = newFilters.category;
    if (newFilters.nature) urlFilters.nature = newFilters.nature;

    if (Object.keys(urlFilters).length > 0) {
      navigate(`/error-monitor?filter=${JSON.stringify(urlFilters)}`, { replace: true });
    } else {
      navigate('/error-monitor', { replace: true });
    }
  };

  const handleClearErrorCodeFilter = () => {
    handleFilterChange('errorCode', undefined);
  };

  // Bulk delete handlers (SuperAdmin only)
  const handleDeleteSelected = async () => {
    try {
      await dataProvider.deleteMany('error-events', { ids: selection.selectedIds });
      selection.clearSelection();
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      notify(translate('resources.error-monitor.bulkDelete.errorMessage'), { type: 'error' });
      throw err;
    }
  };

  const handleDeleteByFilters = async () => {
    const result = await dataProvider.deleteErrorEventsByFilter(filters);
    selection.clearSelection();
    setReloadKey((k) => k + 1);
    return { deletedCount: result.data.deletedCount };
  };


  // Active filters (errors tab) — excludes the always-present date range.
  const errorsActiveFilterCount = [
    filters.severity,
    filters.category,
    filters.nature,
    filters.groupId,
    filters.hotelId,
    filters.deviceId,
    filters.errorCode,
  ].filter(Boolean).length;

  // Extra right-cluster actions for the errors tab: metadata toggle + bulk delete.
  const errorsExtraActions = (
    <>
      <Button
        onClick={() => setMetadataExpanded(!metadataExpanded)}
        size="small"
        variant={metadataExpanded ? 'contained' : 'outlined'}
        startIcon={metadataExpanded
          ? <CollapseMetadataIcon sx={{ fontSize: '16px !important' }} />
          : <ExpandMetadataIcon sx={{ fontSize: '16px !important' }} />
        }
        sx={{
          fontSize: '0.75rem',
          py: 0.4,
          px: 1.5,
          minHeight: '32px',
          lineHeight: 1.2,
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 600,
          ...(metadataExpanded
            ? {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
              }
            : {
                borderColor: alpha(theme.palette.text.secondary, 0.3),
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }),
        }}
      >
        {translate('resources.events.fields.metadata')}
      </Button>

      {isSuperAdmin && (
        <BulkDeletePillButton
          selectedCount={selection.count}
          onDeleteSelected={handleDeleteSelected}
          onDeleteByFilters={handleDeleteByFilters}
          resourceLabel="error-monitor"
        />
      )}
    </>
  );

  if (!canViewErrors) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {translate('ra.auth.auth_check_error')}
        </Alert>
      </Box>
    );
  }

  return (
    <FilterPopoverProvider>
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        {/* Standard header card: tabs in the left zone; filters in the popover. */}
        <SectionHeader manualScreen="error-monitor"
          helpConfig={errorMonitorHelpConfig}
          left={<HotelScopeSelector scope={scope} />}
          filterContent={(
            <>
              {/* Severity Filter */}
              <TextField
                select
                size="small"
                label={translate('resources.errorMonitor.filters.severity')}
                value={filters.severity || ''}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="">
                  {translate('resources.errorMonitor.filters.allSeverities')}
                </MenuItem>
                {SEVERITY_OPTIONS.map((severity) => (
                  <MenuItem key={severity} value={severity}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: getSeverityColor(severity),
                        }}
                      />
                      {translate(`resources.errorDefinitions.severity.${severity.toLowerCase()}`)}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Category Filter */}
              <TextField
                select
                size="small"
                label={translate('resources.errorMonitor.filters.category')}
                value={filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="">
                  {translate('resources.errorMonitor.filters.allCategories')}
                </MenuItem>
                {CATEGORY_OPTIONS.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {translate(`resources.errorDefinitions.category.${cat.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </TextField>

              {/* Nature Filter */}
              <TextField
                select
                size="small"
                label={translate('resources.errorMonitor.filters.nature')}
                value={filters.nature || ''}
                onChange={(e) => handleFilterChange('nature', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="">
                  {translate('resources.errorMonitor.filters.allNatures')}
                </MenuItem>
                {NATURE_OPTIONS.map((nat) => (
                  <MenuItem key={nat} value={nat}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: NATURE_COLORS[nat],
                        }}
                      />
                      {translate(`resources.errorMonitor.nature.${nat.toLowerCase()}`)}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Group Filter (SuperAdmin and multi-group GroupAdmin) */}
              {showGroupFilter && (
                <TextField
                  select
                  size="small"
                  label={translate('resources.errorMonitor.filters.group')}
                  value={filters.groupId || ''}
                  onChange={(e) => {
                    const newGroupId = e.target.value || undefined;
                    const newFilters = {
                      ...filters,
                      groupId: newGroupId,
                      hotelId: undefined,
                      deviceId: undefined,
                    };
                    setFilters(newFilters);

                    const urlFilters: Partial<ErrorFilters> = {};
                    if (newFilters.severity) urlFilters.severity = newFilters.severity;
                    if (newFilters.category) urlFilters.category = newFilters.category;
                    if (newFilters.nature) urlFilters.nature = newFilters.nature;
                    if (newGroupId) urlFilters.groupId = newGroupId;
                    if (newFilters.errorCode) urlFilters.errorCode = newFilters.errorCode;
                    if (Object.keys(urlFilters).length > 0) {
                      navigate(`/error-monitor?filter=${JSON.stringify(urlFilters)}`, { replace: true });
                    } else {
                      navigate('/error-monitor', { replace: true });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">
                    {translate('resources.errorMonitor.filters.allGroups')}
                  </MenuItem>
                  {groups.map((group: any) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                size="small"
                label={translate('resources.errorMonitor.filters.hotel')}
                value={filters.hotelId || ''}
                onChange={(e) => {
                  const newHotelId = e.target.value || undefined;
                  const newFilters = {
                    ...filters,
                    hotelId: newHotelId,
                    deviceId: undefined,
                  };
                  setFilters(newFilters);

                  const urlFilters: Partial<ErrorFilters> = {};
                  if (newFilters.groupId) urlFilters.groupId = newFilters.groupId;
                  if (newHotelId) urlFilters.hotelId = newHotelId;
                  if (newFilters.severity) urlFilters.severity = newFilters.severity;
                  if (newFilters.category) urlFilters.category = newFilters.category;
                  if (newFilters.nature) urlFilters.nature = newFilters.nature;
                  if (newFilters.errorCode) urlFilters.errorCode = newFilters.errorCode;
                  if (Object.keys(urlFilters).length > 0) {
                    navigate(`/error-monitor?filter=${JSON.stringify(urlFilters)}`, { replace: true });
                  } else {
                    navigate('/error-monitor', { replace: true });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">
                  {translate('resources.errorMonitor.filters.allHotels')}
                </MenuItem>
                {hotels.map((hotel: any) => (
                  <MenuItem key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Device Filter */}
              <TextField
                select
                size="small"
                label={translate('resources.errorMonitor.filters.device')}
                value={filters.deviceId || ''}
                onChange={(e) => handleFilterChange('deviceId', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">
                  {translate('resources.errorMonitor.filters.allDevices')}
                </MenuItem>
                {/* Error events carry the installation code, so that is the filter value */}
                {devices.map((device: any) => (
                  <MenuItem key={device.id} value={device.installationCode}>
                    {deviceLabel(device)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                type="date"
                size="small"
                label={translate('resources.errorMonitor.filters.dateFrom')}
                value={filters.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />

              <TextField
                type="date"
                size="small"
                label={translate('resources.errorMonitor.filters.dateTo')}
                value={filters.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />

              {/* View mode toggle — list vs grouped by device */}
              <FormControlLabel
                control={
                  <Switch
                    checked={viewMode === 'grouped'}
                    onChange={(e) => setViewMode(e.target.checked ? 'grouped' : 'list')}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupIcon sx={{ fontSize: 14, color: viewMode === 'grouped' ? 'primary.main' : 'text.secondary' }} />
                    <Typography sx={{ fontSize: '0.75rem' }}>
                      {translate('resources.errorMonitor.views.byDevice')}
                    </Typography>
                  </Box>
                }
                sx={{ ml: 1, mr: 0 }}
              />
            </>
          )}
          activeFilterCount={errorsActiveFilterCount}
          extraActions={errorsExtraActions}
        />

        <>
          {/* Active filters chips */}
          {(filters.errorCode || filters.deviceId) && (
            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filters.errorCode && (
                <Chip
                  label={`${translate('resources.errorMonitor.filters.errorCode')}: ${filters.errorCode}`}
                  onDelete={handleClearErrorCodeFilter}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.deviceId && (
                <Chip
                  label={`${translate('resources.errorMonitor.filters.device')}: ${deviceLabel(
                    devices.find((d: any) => d.installationCode === filters.deviceId) ?? { installationCode: filters.deviceId }
                  )}`}
                  onDelete={() => handleFilterChange('deviceId', undefined)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          )}

          {/* Content */}
          <ErrorEventsList filters={filters} viewMode={viewMode} metadataExpanded={metadataExpanded} selection={selection} reloadKey={reloadKey} />
        </>
      </Box>
    </FilterPopoverProvider>
  );
};

export default ErrorMonitor;
